import * as signalR from "@microsoft/signalr";
import type { ChangeNotification } from "../api/generated/contracts";
import { getApiConfig } from "../api/httpConfig";

/** BroadcastAccessor.InvalidationMethodName - the one client-side method name the hub ever invokes. */
export const INVALIDATION_METHOD = "portfolioInvalidated";

/** Composition/PortalHost.RealtimeRoute - the SignalR hub mapped over PortfolioNotificationHub. */
const HUB_PATH = "/hubs/portfolio";

export type InvalidationHandler = (notification: ChangeNotification) => void;

/**
 * Thin wrapper over @microsoft/signalr with automatic reconnect and bearer-token auth (the same
 * token http.ts uses, via httpConfig's getAccessToken - the hub requires authentication at the
 * handshake, RealtimeEndpoints.cs's RequireAuthorization()). Clients connect and receive; they
 * never invoke a hub method (decomposition.md section 8.5) - this wrapper exposes no send.
 */
export class RealtimeClient {
  private connection: signalR.HubConnection | null = null;
  private readonly handlers = new Set<InvalidationHandler>();

  start(): signalR.HubConnection {
    if (this.connection) return this.connection;

    const config = getApiConfig();
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${config.baseUrl}${HUB_PATH}`, {
        accessTokenFactory: async () => (await config.getAccessToken()) ?? "",
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on(INVALIDATION_METHOD, (notification: ChangeNotification) => {
      for (const handler of this.handlers) handler(notification);
    });

    this.connection = connection;
    void connection.start().catch(() => {
      // Automatic reconnect handles transient failures; a start-time failure is retried by the
      // caller re-mounting the provider, which is an acceptable v1 posture for a channel whose
      // own freshness poll (roadmap.getFreshness) is the correctness backstop.
    });

    return connection;
  }

  subscribe(handler: InvalidationHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  async stop(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    this.handlers.clear();
    if (connection) {
      await connection.stop();
    }
  }
}

export const realtimeClient = new RealtimeClient();
